import axios from "axios";
import { API_BASE_URL } from "../config";

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("admin_token");

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// A single in-flight refresh shared by every request that hits a 401 at the
// same time (e.g. AdminLayout's poll + a page's own fetches all firing on
// mount) - without this, each would trigger its own refresh call.
let refreshPromise = null;

function clearAdminSessionAndRedirect() {
  localStorage.removeItem("admin_token");
  localStorage.removeItem("admin_user");

  if (
    window.location.pathname.startsWith("/admin") &&
    window.location.pathname !== "/admin/login"
  ) {
    window.location.href = "/admin/login";
  }
}

// The admin JWT is short-lived (JWT_TTL, currently 8h) so any admin who
// leaves the panel open for a few hours comes back to an expired token -
// AdminProtectedRoute only checks that a token exists, not that it's still
// valid, so the UI renders as "logged in" while every request 401s and
// pages are left showing no data with no indication why. This retries a
// 401'd admin request once, transparently, via POST /refresh (which - unlike
// every other admin endpoint - accepts an already-expired token as long as
// it's within JWT_REFRESH_TTL and not blacklisted). Only a genuinely
// unrecoverable token (past the refresh window, or none at all) forces a
// real re-login.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const hadAdminAuth = !!originalRequest?.headers?.Authorization;

    // Anonymous storefront requests never carry an admin token, so a 401
    // there has nothing to do with admin session expiry - leave it alone.
    if (status !== 401 || !hadAdminAuth) {
      return Promise.reject(error);
    }

    const isAuthEndpoint =
      originalRequest.url?.includes("/refresh") || originalRequest.url?.includes("/login");

    if (isAuthEndpoint || originalRequest._retriedAfterRefresh) {
      clearAdminSessionAndRedirect();
      return Promise.reject(error);
    }

    originalRequest._retriedAfterRefresh = true;

    try {
      if (!refreshPromise) {
        refreshPromise = axios
          .post(
            `${API_BASE_URL}/api/refresh`,
            null,
            { headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } }
          )
          .finally(() => {
            refreshPromise = null;
          });
      }

      const refreshRes = await refreshPromise;
      const newToken = refreshRes.data?.access_token;

      if (!newToken) {
        throw new Error("Refresh response had no access_token");
      }

      localStorage.setItem("admin_token", newToken);
      if (refreshRes.data?.user) {
        localStorage.setItem("admin_user", JSON.stringify(refreshRes.data.user));
      }

      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return api(originalRequest);
    } catch (refreshErr) {
      clearAdminSessionAndRedirect();
      return Promise.reject(error);
    }
  }
);

export default api;
